<template>
  <div
    v-if="selectedStudents.length > 0"
    class="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-6 z-50 max-w-full sm:max-w-xl bg-[#0B1120] text-white rounded-2xl border border-[#263449] shadow-2xl p-3.5 sm:p-5 animate-slide-up backdrop-blur-md"
  >
    <div class="flex flex-col gap-3 sm:gap-4">
      <div class="flex items-center gap-2.5 sm:gap-3">
        <div class="p-2 bg-indigo-600 rounded-xl flex-shrink-0 shadow-xs">
          <CheckSquare class="w-4 h-4 md:w-5 md:h-5 text-white" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm sm:text-base font-bold text-white tracking-tight">
            {{ selectedStudents.length }} student{{
              selectedStudents.length > 1 ? "s" : ""
            }}
            selected
          </p>
          <p class="text-xs text-slate-300">
            Choose an action to perform on selected students
          </p>
        </div>
      </div>
      <div
        class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-2.5"
      >
        <button
          @click="$emit('delete-selected')"
          :disabled="isLoading || isDeleting || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3.5 sm:px-4 py-2 bg-rose-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-rose-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
        >
          <AppLoader
            v-if="isDeleting"
            variant="button"
            class="w-3.5 h-3.5 text-white"
          />
          <Trash2
            v-else
            class="w-3.5 h-3.5"
          />
          <span>{{ isDeleting ? "Deleting..." : "Delete Selected" }}</span>
        </button>

        <!-- Book Smart Card button -->
        <button
          @click="$emit('book-smart-card')"
          :disabled="isLoading || isDeleting || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3.5 sm:px-4 py-2 bg-indigo-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
        >
          <AppLoader
            v-if="isBookingSmartCard"
            variant="button"
            class="w-3.5 h-3.5 text-white"
          />
          <CreditCard
            v-else
            class="w-3.5 h-3.5"
          />
          <span>{{ isBookingSmartCard ? "Processing..." : "Order Cards" }}</span>
        </button>

        <button
          @click="showPromoteClassModal = true"
          :disabled="isLoading || isBookingSmartCard"
          class="inline-flex items-center justify-center gap-2 px-3.5 sm:px-4 py-2 bg-emerald-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
        >
          <TrendingUp class="w-3.5 h-3.5" />
          <span>Promote Class</span>
        </button>

        <button
          @click="$emit('clear-selection')"
          class="px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800/80 rounded-xl transition-all duration-200 border border-slate-700"
        >
          Clear Selection
        </button>
      </div>
    </div>
  </div>

  <!-- Promote Class Modal -->
  <PromoteClassModal
    :show="showPromoteClassModal"
    :selectedStudentsCount="selectedStudents.length"
    :isPromoting="isLoading"
    @close="showPromoteClassModal = false"
    @confirm-promote="handleConfirmPromote"
  />
</template>

<script setup>
  import { ref } from "vue";
  import { CheckSquare, TrendingUp, Trash2, CreditCard } from "lucide-vue-next";
  import PromoteClassModal from "./PromoteClassModal.vue";

  const props = defineProps({
    selectedStudents: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isDeleting: {
      type: Boolean,
      default: false,
    },
    isBookingSmartCard: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    "clear-selection",
    "promote-selected",
    "delete-selected",
    "promote-selected-with-class",
    "book-smart-card",
  ]);

  const showPromoteClassModal = ref(false);

  const handleConfirmPromote = ({ classId, session, year }) => {
    emit("promote-selected-with-class", {
      studentIds: props.selectedStudents,
      classId,
      session,
      year,
    });
    showPromoteClassModal.value = false;
  };
</script>

<style scoped>
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out forwards;
  }
</style>
